<template>
  <div class="v2-add-order-products">
    <div class="v2-add-order-products__toolbar">
      <div class="v2-add-order-products__search">
        <input
          type="text"
          class="v2-add-order-products__search-input"
          placeholder="Search by products, style code..."
          @input="searchProducts($event.target.value)"
        >
      </div>
      <v2-button
        v-if="!!payloadProducts.length"
        variant="primary"
        :disabled="isAddingProducts"
        @click="onClickAddToOrder"
      >
        <template v-if="!isAddingProducts">
          Add to the order
        </template>
        <v2-spinner
          v-else
          size="sm"
        />
      </v2-button>
    </div>

    <template v-if="!!shopProducts.length">
      <div
        ref="scrollContainer"
        class="v2-add-order-products__list"
        @scroll="loadMoreProducts"
      >
        <div
          v-for="product in shopProducts"
          :key="product._id"
          class="v2-add-order-products__card"
          :class="{
            'v2-add-order-products__card--selected': selectedProductIds.includes(product._id),
            'v2-add-order-products__card--error': product.hasError,
          }"
        >
          <div class="v2-add-order-products__layout">
            <!-- Image -->
            <div class="v2-add-order-products__image">
              <img
                :src="parseProductImage(product)"
                :alt="product.name"
                class="v2-add-order-products__img"
                @error="imageLoadError"
              >
            </div>

            <!-- Info -->
            <div class="v2-add-order-products__info">
              <h4 class="v2-add-order-products__name">
                {{ product.name }}
              </h4>
              <p class="v2-add-order-products__breadcrumb">
                {{ [product.department, product.category, product.subCategory].filter(Boolean).join(' / ') }}
              </p>
              <div class="v2-add-order-products__details">
                <div class="v2-add-order-products__detail-row">
                  <span>Style Code:</span>
                  <strong>{{ product.styleCode }}</strong>
                </div>
                <div class="v2-add-order-products__detail-row">
                  <span>Retail Price:</span>
                  <strong>{{ formatCurrency(product.retailPrice, product.currency) }}</strong>
                </div>
                <div class="v2-add-order-products__detail-row">
                  <span>Wholesale Selling Price:</span>
                  <strong>{{ formatCurrency(product.price, product.currency) }}</strong>
                </div>
                <div
                  v-if="product.shippingStartDate && product.shippingEndDate"
                  class="v2-add-order-products__detail-row"
                >
                  <span>Availability Date:</span>
                  <strong>{{ formattedDate(product.shippingStartDate) }} to {{ formattedDate(product.shippingEndDate) }}</strong>
                </div>
              </div>

              <!-- Carton qty -->
              <div
                v-if="product.isCarton"
                class="v2-add-order-products__carton-qty"
              >
                <span>Qty:</span>
                <input
                  v-model.number="product.quantity"
                  type="number"
                  class="v2-add-order-products__qty-input"
                  :max="getMaxQuantity(product.quantityAvailable)"
                  :disabled="isProductValueDisabled(product.quantityAvailable)"
                  min="1"
                  @input="updateQuantity(product)"
                >
                <span
                  v-if="hasProductMaxQuantity(product.quantityAvailable)"
                  class="v2-add-order-products__max-qty"
                >
                  Max Qty: {{ product.quantityAvailable }}
                </span>
              </div>

              <!-- Variants -->
              <product-variants
                :is-deleted="product.isDeleted"
                :is-carton="product.isCarton"
                :items="product.items"
                :is-edit-enabled="true"
                @update-variants="(variants) => updateProductVariants(variants, product)"
              />
            </div>

            <!-- Actions -->
            <div class="v2-add-order-products__actions">
              <label class="v2-add-order-products__checkbox">
                <input
                  type="checkbox"
                  :checked="selectedProductIds.includes(product._id)"
                  @change="onSelectProduct($event.target.checked, product)"
                >
              </label>
              <div class="v2-add-order-products__totals">
                <div class="v2-add-order-products__total-value">
                  Total Value:
                  {{ formatCurrency(product.lineTotalPriceWithDiscount || product.lineTotalPrice || 0, product.currency) }}
                </div>
                <div class="v2-add-order-products__total-units">
                  Total Units: {{ formatNumber(product.lineTotalQuantity || 0) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <div
        v-if="isLoadingProducts"
        class="v2-flex v2-justify-center v2-p-8"
      >
        <v2-spinner label="Loading products..." />
      </div>
      <div
        v-else
        class="v2-add-order-products__empty"
      >
        No products found
      </div>
    </template>
  </div>
</template>

<script>
import V2Button from '@/components/v2/V2Button.vue'
import V2Spinner from '@/components/v2/V2Spinner.vue'
import ProductVariants from '@/views/apps/product/detail/ProductVariants.vue'
import {
  formatNumber,
  getDefaultProductImage,
  getMaxQuantity,
  hasProductMaxQuantity,
  imageLoadError,
  isProductValueDisabled,
} from '@/@core/utils/utils'
import { utils, productUtil } from '@kingpin-global/kingpin-utils-frontend'
import { FETCH_COLLECTION_PRODUCTS } from '@/store/modules/collection.module'
import { ADD_ORDER_PRODUCTS } from '@/store/modules/order.module'
import { apiToastSuccess, apiToastWarning } from '@/@core/utils/toast'
import { parseProductItems } from '@/views/apps/checkout/cart-products-utils'

const { getProductAndCartTotals } = productUtil
const { formatCurrency, formattedDate } = utils

function parseProductImage(product) {
  if (product?.thumbnail) {
    return product.thumbnail
  }
  if (product?.images?.length && product?.images[0]) {
    return product.images[0]
  }
  return getDefaultProductImage()
}

function buildCartonPayload(product) {
  return {
    productId: product._id,
    quantity: product.quantity,
  }
}

function extractVariantItems(variants) {
  const items = []
  variants.forEach(item => {
    const keys = Object.keys(item).filter(k => k !== 'color')
    keys.forEach(k => {
      items.push({
        sku: item[k].skuId,
        value: item[k].value,
      })
    })
  })
  return items
}

function buildVariantPayload(product) {
  const hasVariants = Array.isArray(product.variants) && product.variants.length > 0
  if (!hasVariants) {
    return null
  }
  return {
    productId: product._id,
    items: extractVariantItems(product.variants),
  }
}

function buildPayloadProduct(product) {
  if (product.isCarton) {
    return buildCartonPayload(product)
  }
  return buildVariantPayload(product)
}

function hasAnyNonCartonProductWithNoValues(payloadProducts) {
  return payloadProducts.some(p => {
    const hasItems = p.items && p.items.length
    return hasItems && p.items.every(item => item.value === 0)
  })
}

function haveItemsChanged(newItems, prevItems) {
  if (newItems === prevItems) return false
  if (!prevItems || prevItems.length !== newItems.length) return true
  return newItems.some((item, idx) => item !== prevItems[idx])
}

function isAtScrollBottom(el) {
  return el && el.scrollTop + el.clientHeight >= el.scrollHeight
}

export default {
  name: 'V2AddOrderProducts',
  components: {
    V2Button,
    V2Spinner,
    ProductVariants,
  },
  props: {
    order: {
      type: Object,
      default: () => null,
    },
    collectionId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      shopProducts: [],
      isLoadingProducts: false,
      formatNumber,
      formatCurrency,
      formattedDate,
      parseProductImage,
      hasProductMaxQuantity,
      getMaxQuantity,
      imageLoadError,
      isProductValueDisabled,
      searchText: '',
      pageNumber: 0,
      totalProductsCount: 0,
      selectedProductIds: [],
      payloadProducts: [],
      isAddingProducts: false,
      debounce: null,
      existingProductIds: [],
    }
  },
  created() {
    if (this.order) {
      this.existingProductIds = this.order.products.map(p => p.productId)
    }
    if (this.collectionId) {
      this.loadProducts()
    }
  },
  methods: {
    async onClickAddToOrder() {
      if (hasAnyNonCartonProductWithNoValues(this.payloadProducts)) {
        apiToastWarning('Before proceeding, please verify that there are no values for some selected products.')
        return
      }

      this.isAddingProducts = true
      try {
        const res = await this.$store.dispatch(ADD_ORDER_PRODUCTS, {
          orderId: this.order._id,
          payload: { products: this.payloadProducts },
        })
        // Backend currently returns `{}` for data on success, so guard against
        // a missing message rather than dereferencing it blindly.
        apiToastSuccess(res?.data?.message || 'Products added successfully!')
        // Hand off the reload to the parent — it owns the orderId and the
        // page-level loading state.
        this.$emit('products-added')
      } finally {
        // Always release the local spinner so the button never stays stuck
        // even if a downstream toast/emit/handler throws.
        this.isAddingProducts = false
      }
    },
    loadMoreProducts() {
      this.$nextTick(() => {
        if (!isAtScrollBottom(this.$refs.scrollContainer)) return
        if (this.shopProducts.length >= this.totalProductsCount) return
        this.pageNumber++
        this.loadProducts()
      })
    },
    buildLoadProductsParams() {
      const params = {
        collectionId: this.collectionId,
        search: this.searchText,
        page: this.pageNumber,
        limit: 10,
      }
      // Forward the retailerId so the catalog returns retailer-specific
      // pricing and transaction currency (handled server-side via
      // getProductsByRetailerId on GET /v2/catalog/products, and aligned
      // with the orders-service KP-3223 fix that switched
      // addProductsToOrder to getProductsWithPrices). Without this the
      // modal shows generic currency/price instead of the retailer's
      // negotiated values.
      if (this.order?.retailerId) {
        params.retailerId = this.order.retailerId
      }
      return params
    },
    isNewProductForOrder(product) {
      return !this.existingProductIds.includes(product._id)
    },
    applyLoadProductsResponse(res) {
      const responseData = res.data?.data
      const responseProducts = responseData?.products?.[0]?.products ?? []
      this.totalProductsCount = responseData?.count
      const newProducts = []
      responseProducts.forEach(product => {
        product.quantity = product.quantity || 1
        if (this.isNewProductForOrder(product)) {
          newProducts.push(product)
        }
      })
      this.shopProducts = [...this.shopProducts, ...newProducts]
    },
    loadProducts() {
      this.isLoadingProducts = true
      this.$store
        .dispatch(FETCH_COLLECTION_PRODUCTS, this.buildLoadProductsParams())
        .then(res => {
          this.applyLoadProductsResponse(res)
          this.isLoadingProducts = false
        })
        .catch(() => {
          this.isLoadingProducts = false
        })
    },
    onSelectProduct(isSelected, product) {
      if (isSelected) {
        this.selectedProductIds.push(product._id)
        this.addProductToPayload(product, true)
      } else {
        this.selectedProductIds = this.selectedProductIds.filter(id => id !== product._id)
        this.removeProductFromPayload(product)
      }
    },
    searchProducts(text) {
      this.searchText = text
      clearTimeout(this.debounce)
      this.debounce = setTimeout(() => {
        this.pageNumber = 0
        this.shopProducts = []
        this.loadProducts()
      }, 500)
    },
    updateQuantity(product) {
      const index = this.shopProducts.findIndex(p => p._id === product._id)
      this.shopProducts[index].quantity = product.quantity
      this.updateTotalPriceAndUnits(product)
    },
    updateProductVariants(variants, product) {
      product.variants = variants
      const items = parseProductItems(product)
      // Guard: only replace product.items when the underlying item objects have
      // actually changed.  Prevents infinite re-render caused by the
      // ProductVariantsMixin items watcher firing on a new array reference.
      if (haveItemsChanged(items, product.items)) {
        product.items = items
        if (!product.isCarton) {
          product.hasError = product?.hasError || false
        }
      }
      this.updateTotalPriceAndUnits(product)
    },
    updateTotalPriceAndUnits(product) {
      getProductAndCartTotals(this.shopProducts, this.shopProducts[0].discount)
      // getProductAndCartTotals mutates lineTotalPrice/lineTotalQuantity directly
      // on product objects. Vue 2 doesn't detect these changes, so force reactivity
      // by splicing the product back into the array.
      const idx = this.shopProducts.indexOf(product)
      if (idx !== -1) {
        this.shopProducts.splice(idx, 1, product)
      }
      this.addProductToPayload(product)
    },
    addProductToPayload(product, isAdd = false) {
      const payloadProduct = buildPayloadProduct(product)
      if (!payloadProduct) {
        return
      }

      if (isAdd) {
        this.payloadProducts.push(payloadProduct)
        return
      }

      const isProductAlreadySelected = this.selectedProductIds.includes(product._id)
      if (isProductAlreadySelected) {
        const index = this.payloadProducts.findIndex(p => p.productId === product._id)
        this.payloadProducts[index] = payloadProduct
      }
    },
    removeProductFromPayload(product) {
      this.payloadProducts = this.payloadProducts.filter(
        payloadProduct => payloadProduct.productId !== product._id,
      )
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/v2/_glass-mixins.scss';

.v2-add-order-products {
  &__toolbar {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.75rem 0;
    position: sticky;
    top: 0;
    background: var(--v2-card);
    z-index: 10;
  }

  &__search {
    flex: 1;
  }

  &__search-input {
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--v2-border);
    border-radius: 0.5rem;
    font-size: 0.875rem;
    color: var(--v2-foreground);
    background: var(--v2-background);

    &:focus {
      outline: none;
      border-color: var(--v2-primary);
    }

    &::placeholder {
      color: var(--v2-muted-foreground);
    }
  }

  &__list {
    max-height: calc(100vh - 220px);
    overflow-y: auto;
    padding: 0.5rem 0;
  }

  &__card {
    // Canonical no-backdrop-filter glass (mixin in _glass-mixins.scss).
    // Add Products list scrolls 50+ cards inside a modal — page mesh
    // substrate provides the optical blur; `contain: layout` skips
    // layout work on off-screen cards during fast scroll.
    @include v2-glass-panel-flat;
    border-radius: 0.625rem;
    margin-bottom: 0.75rem;
    overflow: hidden;
    contain: layout;

    &--selected {
      border-color: var(--v2-primary);
    }

    &--error {
      border-color: var(--v2-destructive);
    }
  }

  &__layout {
    display: grid;
    grid-template-columns: 200px 1fr 200px;
    gap: 1rem;
    padding: 0 1.25rem 0 0;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      padding: 1rem;
    }
  }

  &__image {
    aspect-ratio: 2/3;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  &__img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
    border-radius: 0.625rem 0 0 0.625rem;
    background: #fff;
  }

  &__info {
    flex: 1;
    min-width: 0;
    padding: 1rem 0;
    border-right: 1px solid var(--v2-border);
    padding-right: 1rem;
    // ProductVariants b-table overrides live in _components.scss (global)
  }

  &__name {
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--v2-foreground);
    margin: 0;
  }

  &__breadcrumb {
    font-size: 0.75rem;
    color: var(--v2-muted-foreground);
    margin: 0.25rem 0 0.5rem;
  }

  &__details {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
  }

  &__detail-row {
    font-size: 0.8125rem;
    color: var(--v2-muted-foreground);
    display: flex;
    align-items: center;
    gap: 0.25rem;

    strong {
      color: var(--v2-foreground);
    }
  }

  &__carton-qty {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.5rem;
    font-size: 0.8125rem;
  }

  &__qty-input {
    width: 80px;
    padding: 0.25rem 0.5rem;
    border: 1px solid var(--v2-border);
    border-radius: 0.375rem;
    font-size: 0.8125rem;
    text-align: center;

    &:focus {
      outline: none;
      border-color: var(--v2-primary);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__max-qty {
    font-size: 0.75rem;
    color: var(--v2-muted-foreground);
  }

  &__actions {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.75rem;
    min-width: 100px;
    padding-top: 1rem;
  }

  &__checkbox {
    cursor: pointer;
    align-self: flex-end;

    input {
      width: 18px;
      height: 18px;
      accent-color: var(--v2-primary);
    }
  }

  &__totals {
    text-align: center;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin: auto 0;
    align-self: center;
  }

  &__total-value {
    font-size: 1rem;
    font-weight: 700;
    color: var(--v2-foreground);
    margin-bottom: 0.5rem;
  }

  &__total-units {
    font-size: 0.8125rem;
    color: var(--v2-muted-foreground);
  }

  &__empty {
    text-align: center;
    padding: 2rem;
    color: var(--v2-muted-foreground);
    font-size: 0.875rem;
  }
}
</style>
